// SPDX-License-Identifier: MIT
// The deep discovery phase, FOLDED into the progressive chat (guided first-run beat ④).
// Operator correction 2026-06-23: the discovery (summary + 3-5 file-grounded questions
// + save + « Régénérer avec mes réponses » + « Refaire la découverte ») was under-
// represented; it must be faithfully present yet « bien fondue » — volume carried by
// progressive reveal (one question at a time) + a compact recap strip, NOT a textarea
// wall. Dedicated component so the live discovery-chat.ts (open-project converse path)
// stays untouched; it consumes the SAME proven callbacks the welcome.ts discovery
// wiring already owns (onFinishDiscovery → regen, onSaveDraft, onRestartDiscovery).
// Reuses the real discovery.* i18n verbatim. Styles: guided-first-run.css.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { renderWhyBubble, type WhyBubbleElement } from './why-bubble';

export interface FoldedDiscoveryQuestion {
  id: string;
  index: number;
  total: number;
  source: string;   // file provenance, e.g. "tirée de l'ordonnance"
  text: string;
  why?: string;     // file-grounded « pourquoi » (overrides the generic copy)
}

export interface FoldedDiscoveryOptions {
  summary: string;                         // discovery_summary, grounded in real content
  total: number;                           // total questions
  answered?: number;                       // answered so far (recap)
  skipped?: { name: string; reason: string }[]; // attachments Claude could not read
  onAnswer?(text: string): void | Promise<void>; // composer send (the Q&A loop); absent → display-only
  onRegenerate(): void | Promise<void>;    // 💬 Régénérer avec mes réponses
  onSaveDraft(): void | Promise<void>;     // 💾 Enregistrer mes réponses
  onRestart(): void | Promise<void>;       // 🔄 Refaire la découverte
}

export type FoldedDiscoveryElement = HTMLElement & {
  __appendQuestion(q: FoldedDiscoveryQuestion): void;
  __appendAnswer(text: string): void;
  __appendAck(text?: string): void;
  __appendFold(text: string): void;        // a collapsed "✓ question N répondue" marker pill
  __renderRecap(answered: number, total: number): void;
  __setComposerEnabled(on: boolean): void; // lock the composer during regen / when done
  __setAnswersRecap(items: { q: string; a: string }[]): void; // feeds the « voir mes réponses » review
  __collapseOpenWhy(): void; // collapse the currently-open why-bubble (advance / terminal)
};

export function renderFoldedDiscovery(opts: FoldedDiscoveryOptions): FoldedDiscoveryElement {
  const root = document.createElement('section') as FoldedDiscoveryElement;
  // `gfr` makes the component SELF-SCOPING: guided-first-run.css defines its token
  // aliases (--paper/--sans/--serif/…) AND its descendant selectors (.gfr .disc-*)
  // under `.gfr`. In the harness the beats sit inside a .gfr wrapper, but in the
  // PRODUCTION card the folded discovery mounts with no .gfr ancestor — so the root
  // must carry `.gfr` itself or it renders unstyled (the harness-vs-prod fidelity trap).
  root.className = 'folded-discovery gfr';
  root.setAttribute('data-component', 'folded-discovery');
  root.innerHTML = `
    <div class="disc-head">
      <span class="disc-chip">${escapeHtml(t('discovery.chip'))}</span>
      <span class="disc-title">${escapeHtml(t('discovery.title'))}</span>
    </div>
    <div class="thread">
      <div class="msg msg--bot"><span class="who">${escapeHtml(t('onboarding.discSummaryWho'))}</span>${escapeHtml(opts.summary)}</div>
    </div>
    <div class="disc-recap" data-component="disc-recap"></div>
    <div class="disc-answers" data-component="disc-answers" hidden></div>
    <div class="thread" data-component="disc-thread"></div>
    ${opts.onAnswer ? `
    <div class="composer" data-component="disc-composer">
      <input type="text" data-component="disc-answer-input" placeholder="${escapeHtml(t('discovery.answerPlaceholder'))}" aria-label="${escapeHtml(t('discovery.answerSend'))}" />
      <button type="button" class="send" data-component="disc-answer-send" aria-label="${escapeHtml(t('discovery.answerSend'))}">↑</button>
    </div>` : ''}
    <div class="disc-foot">
      <div class="disc-cta-row">
        <button type="button" class="disc-regen" data-component="disc-regen">${escapeHtml(t('discovery.submit'))}</button>
        <button type="button" class="disc-ghost" data-component="disc-save">${escapeHtml(t('discovery.saveDraft'))}</button>
        <button type="button" class="disc-ghost" data-component="disc-rerun">${escapeHtml(t('discovery.rerun'))}</button>
      </div>
      <span class="disc-rerun-hint">${escapeHtml(t('discovery.rerunHint'))}</span>
      ${opts.skipped && opts.skipped.length ? `
      <details class="skipped" data-component="disc-skipped">
        <summary>${escapeHtml(t('discovery.skippedSummary').replace('{n}', String(opts.skipped.length)))}</summary>
        <ul>${opts.skipped.map((s) => `<li><code>${escapeHtml(s.name)}</code> — ${escapeHtml(s.reason)}</li>`).join('')}</ul>
      </details>` : ''}
    </div>
  `;

  const recap = root.querySelector<HTMLElement>('[data-component="disc-recap"]')!;
  const answersBox = root.querySelector<HTMLElement>('[data-component="disc-answers"]')!;
  const thread = root.querySelector<HTMLElement>('[data-component="disc-thread"]')!;
  // Current-only fold: at most one why-bubble is ever "open" (the current
  // question's). This tracks it so __appendQuestion/__collapseOpenWhy can
  // collapse it unconditionally on every advance and at the terminal state.
  let lastWhy: WhyBubbleElement | null = null;

  // « voir mes réponses » must reveal something real, never be a dead button. The
  // disc-see button is re-created on every __renderRecap, so its visibility is
  // re-synced there (hidden until there is at least one answer to review).
  let reviewCount = 0;
  const syncSeeVisibility = (): void => {
    const see = root.querySelector<HTMLElement>('[data-component="disc-see"]');
    if (see) see.hidden = reviewCount === 0;
  };

  root.__renderRecap = (answered, total): void => {
    const dots = Array.from({ length: total }, (_v, i) => `<i class="${i < answered ? 'on' : ''}"></i>`).join('');
    recap.innerHTML = `
      <span class="disc-dots">${dots}</span>
      <span>${answered} / ${total} ${escapeHtml(t('onboarding.discRepondu'))}</span>
      <span class="disc-saved">${escapeHtml(t('onboarding.discAuto'))}</span>
      <button type="button" class="disc-see" data-component="disc-see" hidden>${escapeHtml(t('onboarding.discSee'))}</button>
    `;
    syncSeeVisibility();
  };

  root.__appendQuestion = (q): void => {
    // Current-only fold: collapse whatever was open BEFORE this new question,
    // regardless of whether the new one carries a why (interleaving-safe).
    lastWhy?.__collapse();
    lastWhy = null;
    const bubble = document.createElement('div');
    bubble.className = 'msg msg--bot msg--q';
    bubble.innerHTML = `<span class="who">✦ ${escapeHtml(t('onboarding.discQuestion'))} ${q.index} / ${q.total} · ${escapeHtml(q.source)}</span><span class="qtext">${escapeHtml(q.text)}</span>`;
    thread.appendChild(bubble);
    if (q.why?.trim()) {
      const wb = renderWhyBubble({ pieceId: 'guidedQuestion', autoOpen: true, body: q.why });
      thread.appendChild(wb);
      lastWhy = wb;
    }
    root.scrollTop = root.scrollHeight;
  };
  root.__appendAnswer = (text): void => {
    const b = document.createElement('div');
    b.className = 'msg msg--user';
    b.textContent = text;
    thread.appendChild(b);
  };
  root.__appendAck = (text): void => {
    const b = document.createElement('div');
    b.className = 'msg msg--ack';
    b.textContent = text ?? t('onboarding.discAck');
    thread.appendChild(b);
  };
  root.__appendFold = (text): void => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'qfold';
    b.textContent = text;
    thread.appendChild(b);
  };
  root.__collapseOpenWhy = (): void => { lastWhy?.__collapse(); lastWhy = null; };

  root.querySelector<HTMLButtonElement>('[data-component="disc-regen"]')!.addEventListener('click', () => { void opts.onRegenerate(); });
  root.querySelector<HTMLButtonElement>('[data-component="disc-save"]')!.addEventListener('click', () => { void opts.onSaveDraft(); });
  root.querySelector<HTMLButtonElement>('[data-component="disc-rerun"]')!.addEventListener('click', () => { void opts.onRestart(); });

  // The answer composer (Q&A loop). The component echoes the user's answer as a
  // bubble immediately (snappy feel) and emits onAnswer(text); the DRIVER persists
  // it + advances to the next question (ack + fold + next bubble + recap). Absent
  // onAnswer → display-only (no composer rendered), so __setComposerEnabled no-ops.
  const composer = root.querySelector<HTMLElement>('[data-component="disc-composer"]');
  if (composer && opts.onAnswer) {
    const input = composer.querySelector<HTMLInputElement>('[data-component="disc-answer-input"]')!;
    const send = composer.querySelector<HTMLButtonElement>('[data-component="disc-answer-send"]')!;
    const submit = (): void => {
      const text = input.value.trim();
      if (!text) return;
      root.__appendAnswer(text); // echo immediately
      input.value = '';
      void opts.onAnswer!(text);
      input.focus();
    };
    send.addEventListener('click', submit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
    });
    root.__setComposerEnabled = (on): void => {
      input.disabled = !on;
      send.disabled = !on;
    };
  } else {
    root.__setComposerEnabled = (): void => { /* display-only — no composer to lock */ };
  }

  // « voir mes réponses » review — the driver feeds every captured answer so the
  // user can ALWAYS read back what they typed (the chat is append-only, so without
  // this a wrong answer would be unreviewable short of the destructive « Refaire »).
  root.__setAnswersRecap = (items): void => {
    reviewCount = items.length;
    answersBox.innerHTML = items.map((it) =>
      `<div class="disc-answers__item"><span class="disc-answers__q">${escapeHtml(it.q)}</span><span class="disc-answers__a">${escapeHtml(it.a)}</span></div>`,
    ).join('');
    if (!items.length) answersBox.hidden = true;
    syncSeeVisibility();
  };

  // disc-see is re-created on every __renderRecap → delegate the toggle on root.
  root.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-component="disc-see"]')) {
      answersBox.hidden = !answersBox.hidden;
    }
  });

  root.__renderRecap(opts.answered ?? 0, opts.total);
  return root;
}
